import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Slider from '@react-native-community/slider';
import { Svg, Circle, Text as SvgText, Line, Rect, Path } from 'react-native-svg';
import Footer from '../components/Footer';
import { useTheme } from '../contexts/ThemeContext';

const { width: screenWidth } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';
const isMobile = screenWidth < 768;

const CapitalGainsCalculatorScreen = () => {
  const { colors } = useTheme();
  const [purchasePrice, setPurchasePrice] = useState('100000');
  const [salePrice, setSalePrice] = useState('150000');
  const [purchaseDate, setPurchaseDate] = useState('2020-01-01');
  const [saleDate, setSaleDate] = useState('2024-01-01');
  const [assetType, setAssetType] = useState('equity');
  const [taxSlab, setTaxSlab] = useState('30');
  const [indexationYear, setIndexationYear] = useState('2020');
  const [showExemptions, setShowExemptions] = useState(false);
  const [showScenarios, setShowScenarios] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);
  const [results, setResults] = useState(null);

  const assetTypes = [
    {
      type: 'equity',
      name: 'Equity Shares/MF',
      description: 'Listed equity shares, equity mutual funds',
      stcgRate: 15,
      ltcgRate: 10,
      ltcgThreshold: 100000,
      holdingPeriod: 12,
      indexationAllowed: false,
      color: colors.primary,
    },
    {
      type: 'debt',
      name: 'Debt Mutual Funds',
      description: 'Debt funds, bonds, debentures',
      stcgRate: 'slab',
      ltcgRate: 20,
      ltcgThreshold: 0,
      holdingPeriod: 36,
      indexationAllowed: true,
      color: colors.success,
    },
    {
      type: 'real-estate',
      name: 'Real Estate',
      description: 'Land, building, house property',
      stcgRate: 'slab',
      ltcgRate: 20,
      ltcgThreshold: 0,
      holdingPeriod: 24,
      indexationAllowed: true,
      color: colors.warning,
    },
    {
      type: 'gold',
      name: 'Gold/Precious Metals',
      description: 'Gold, silver, jewelry, gold ETFs',
      stcgRate: 'slab',
      ltcgRate: 20,
      ltcgThreshold: 0,
      holdingPeriod: 36,
      indexationAllowed: true,
      color: colors.error,
    },
  ];

  const taxSlabs = [
    { rate: '5', label: '5%', description: 'Income: ₹2.5L - ₹5L' },
    { rate: '20', label: '20%', description: 'Income: ₹5L - ₹10L' },
    { rate: '30', label: '30%', description: 'Income: Above ₹10L' },
  ];

  // Cost Inflation Index data (simplified for demo)
  const costInflationIndex = {
    2001: 100, 2002: 105, 2003: 109, 2004: 113, 2005: 117,
    2006: 122, 2007: 129, 2008: 137, 2009: 148, 2010: 167,
    2011: 184, 2012: 200, 2013: 220, 2014: 240, 2015: 254,
    2016: 264, 2017: 272, 2018: 280, 2019: 289, 2020: 301,
    2021: 317, 2022: 331, 2023: 348, 2024: 363,
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const calculateHoldingPeriod = (purchaseDate, saleDate) => {
    const purchase = new Date(purchaseDate);
    const sale = new Date(saleDate);
    const diffTime = Math.abs(sale - purchase);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const diffMonths = Math.floor(diffDays / 30);
    return { days: diffDays, months: diffMonths };
  };

  const calculateIndexedCost = (purchasePrice, purchaseYear, saleYear) => {
    const purchaseIndex = costInflationIndex[purchaseYear] || 301;
    const saleIndex = costInflationIndex[saleYear] || 363;
    return (purchasePrice * saleIndex) / purchaseIndex;
  };

  const calculateCapitalGains = () => {
    const purchase = parseFloat(purchasePrice) || 0;
    const sale = parseFloat(salePrice) || 0;
    const taxRate = parseFloat(taxSlab) || 0;

    const asset = assetTypes.find(a => a.type === assetType);
    if (!asset) return null;

    const holdingPeriod = calculateHoldingPeriod(purchaseDate, saleDate);
    const isLongTerm = holdingPeriod.months >= asset.holdingPeriod;

    // Calculate gains
    let capitalGain = sale - purchase;
    let indexedCost = purchase;
    let indexationBenefit = 0;

    // Apply indexation for eligible assets and LTCG
    if (isLongTerm && asset.indexationAllowed) {
      const purchaseYear = new Date(purchaseDate).getFullYear();
      const saleYear = new Date(saleDate).getFullYear();
      indexedCost = calculateIndexedCost(purchase, purchaseYear, saleYear);
      indexationBenefit = indexedCost - purchase;
      capitalGain = sale - indexedCost;
    }

    // Calculate tax
    let taxRate_applied = 0;
    let exemptAmount = 0;
    let taxableGain = Math.max(0, capitalGain);

    if (isLongTerm) {
      if (asset.type === 'equity') {
        // LTCG on equity: 10% above ₹1 lakh
        exemptAmount = Math.min(capitalGain, asset.ltcgThreshold);
        taxableGain = Math.max(0, capitalGain - asset.ltcgThreshold);
        taxRate_applied = asset.ltcgRate;
      } else {
        // LTCG on other assets: 20% with indexation
        taxRate_applied = asset.ltcgRate;
      }
    } else {
      // STCG
      if (asset.stcgRate === 'slab') {
        taxRate_applied = taxRate;
      } else {
        taxRate_applied = asset.stcgRate;
      }
    }

    const tax = (taxableGain * taxRate_applied) / 100;
    const netGain = capitalGain - tax;
    const effectiveRate = capitalGain > 0 ? (tax / capitalGain) * 100 : 0;

    // Calculate different scenarios
    const scenarios = [
      {
        name: 'Current Sale',
        salePrice: sale,
        holdingMonths: holdingPeriod.months,
        isLongTerm,
        capitalGain,
        tax,
        netGain,
      },
      {
        name: 'Hold 1 More Year',
        salePrice: sale * 1.08, // Assuming 8% growth
        holdingMonths: holdingPeriod.months + 12,
        isLongTerm: (holdingPeriod.months + 12) >= asset.holdingPeriod,
        capitalGain: (sale * 1.08) - purchase,
        tax: 0, // Will be calculated
        netGain: 0, // Will be calculated
      },
      {
        name: 'Optimal Exit',
        salePrice: sale * 1.15,
        holdingMonths: asset.holdingPeriod,
        isLongTerm: true,
        capitalGain: (sale * 1.15) - purchase,
        tax: 0, // Will be calculated
        netGain: 0, // Will be calculated
      },
    ];

    // Calculate tax for scenarios
    scenarios.forEach((scenario, index) => {
      if (index === 0) return; // Skip current scenario

      let scenarioTaxableGain = Math.max(0, scenario.capitalGain);
      let scenarioTax = 0;

      if (scenario.isLongTerm) {
        if (asset.type === 'equity') {
          const scenarioExempt = Math.min(scenario.capitalGain, asset.ltcgThreshold);
          scenarioTaxableGain = Math.max(0, scenario.capitalGain - asset.ltcgThreshold);
          scenarioTax = (scenarioTaxableGain * asset.ltcgRate) / 100;
        } else {
          scenarioTax = (scenarioTaxableGain * asset.ltcgRate) / 100;
        }
      } else {
        const rate = asset.stcgRate === 'slab' ? taxRate : asset.stcgRate;
        scenarioTax = (scenarioTaxableGain * rate) / 100;
      }

      scenario.tax = scenarioTax;
      scenario.netGain = scenario.capitalGain - scenarioTax;
    });

    return {
      purchase,
      sale,
      capitalGain,
      indexedCost,
      indexationBenefit,
      holdingPeriod,
      isLongTerm,
      taxRate_applied,
      exemptAmount,
      taxableGain,
      tax,
      netGain,
      effectiveRate,
      asset,
      scenarios,
    };
  };

  useEffect(() => {
    const calculatedResults = calculateCapitalGains();
    setResults(calculatedResults);
  }, [purchasePrice, salePrice, purchaseDate, saleDate, assetType, taxSlab]);

  const renderHeader = () => (
    <View style={styles.header}>
      <Text style={styles.headerTitle}>Capital Gains Calculator</Text>
      <Text style={styles.headerSubtitle}>
        Calculate Short-term and Long-term Capital Gains Tax on your investments with indexation benefits
      </Text>
    </View>
  );

  const renderCalculator = () => (
    <View style={styles.calculatorCard}>
      <Text style={styles.cardTitle}>Investment Details</Text>
      
      {/* Asset Type Selection */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Asset Type</Text>
        <View style={styles.assetTypeContainer}>
          {assetTypes.map((asset) => (
            <TouchableOpacity
              key={asset.type}
              style={[
                styles.assetTypeCard,
                assetType === asset.type && styles.assetTypeCardActive,
              ]}
              onPress={() => setAssetType(asset.type)}
            >
              <View style={[styles.assetTypeIndicator, { backgroundColor: asset.color }]} />
              <Text style={[
                styles.assetTypeName,
                assetType === asset.type && styles.assetTypeNameActive,
              ]}>
                {asset.name}
              </Text>
              <Text style={[
                styles.assetTypeDescription,
                assetType === asset.type && styles.assetTypeDescriptionActive,
              ]}>
                {asset.description}
              </Text>
              <Text style={[
                styles.assetTypeRate,
                assetType === asset.type && styles.assetTypeRateActive,
              ]}>
                LTCG: {asset.ltcgRate}% | STCG: {asset.stcgRate === 'slab' ? 'As per slab' : asset.stcgRate + '%'}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Purchase Price */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Purchase Price</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={purchasePrice}
            onChangeText={setPurchasePrice}
            placeholder="100000"
            keyboardType="numeric"
          />
        </View>
        <Text style={styles.helperText}>Original cost of acquisition</Text>
      </View>

      {/* Sale Price */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Sale Price</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={salePrice}
            onChangeText={setSalePrice}
            placeholder="150000"
            keyboardType="numeric"
          />
        </View>
        <Text style={styles.helperText}>Sale consideration received</Text>
      </View>

      {/* Purchase Date */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Purchase Date</Text>
        <TextInput
          style={styles.dateInput}
          value={purchaseDate}
          onChangeText={setPurchaseDate}
          placeholder="2020-01-01"
        />
        <Text style={styles.helperText}>Date of acquisition (YYYY-MM-DD)</Text>
      </View>

      {/* Sale Date */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Sale Date</Text>
        <TextInput
          style={styles.dateInput}
          value={saleDate}
          onChangeText={setSaleDate}
          placeholder="2024-01-01"
        />
        <Text style={styles.helperText}>Date of sale (YYYY-MM-DD)</Text>
      </View>

      {/* Tax Slab */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Your Tax Slab (for STCG)</Text>
        <View style={styles.taxSlabContainer}>
          {taxSlabs.map((slab) => (
            <TouchableOpacity
              key={slab.rate}
              style={[
                styles.taxSlabButton,
                taxSlab === slab.rate && styles.taxSlabButtonActive,
              ]}
              onPress={() => setTaxSlab(slab.rate)}
            >
              <Text style={[
                styles.taxSlabLabel,
                taxSlab === slab.rate && styles.taxSlabLabelActive,
              ]}>
                {slab.label}
              </Text>
              <Text style={[
                styles.taxSlabDescription,
                taxSlab === slab.rate && styles.taxSlabDescriptionActive,
              ]}>
                {slab.description}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </View>
  );

  const renderDonutChart = () => {
    if (!results) return null;

    const { capitalGain, tax, netGain } = results;
    const totalAmount = Math.abs(capitalGain);
    
    if (totalAmount === 0) return null;

    const taxPercentage = (tax / totalAmount) * 100;
    const netPercentage = (Math.abs(netGain) / totalAmount) * 100;

    const size = 200;
    const strokeWidth = 20;
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;

    const taxStrokeDasharray = `${(taxPercentage / 100) * circumference} ${circumference}`;
    const netStrokeDasharray = `${(netPercentage / 100) * circumference} ${circumference}`;
    const netStrokeDashoffset = -((taxPercentage / 100) * circumference);

    return (
      <View style={styles.chartCard}>
        <Text style={styles.cardTitle}>Capital Gains Breakdown</Text>
        <View style={styles.chartContainer}>
          <Svg width={size} height={size}>
            {/* Background circle */}
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.border}
              strokeWidth={strokeWidth}
              fill="transparent"
            />
            
            {/* Tax amount */}
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.error}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={taxStrokeDasharray}
              strokeLinecap="round"
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
            
            {/* Net gain */}
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.success}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={netStrokeDasharray}
              strokeDashoffset={netStrokeDashoffset}
              strokeLinecap="round"
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
            
            {/* Center text */}
            <SvgText
              x={size / 2}
              y={size / 2 - 10}
              textAnchor="middle"
              fontSize="14"
              fontWeight="600"
              fill={colors.text}
            >
              Total Gain
            </SvgText>
            <SvgText
              x={size / 2}
              y={size / 2 + 10}
              textAnchor="middle"
              fontSize="16"
              fontWeight="bold"
              fill={colors.text}
            >
              {formatCurrency(capitalGain)}
            </SvgText>
          </Svg>
        </View>
        
        <View style={styles.chartLegend}>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.error }]} />
            <Text style={styles.legendText}>Tax ({taxPercentage.toFixed(1)}%)</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.success }]} />
            <Text style={styles.legendText}>Net Gain ({netPercentage.toFixed(1)}%)</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderResults = () => {
    if (!results) return null;

    const { 
      purchase,
      sale,
      capitalGain,
      indexedCost,
      indexationBenefit,
      holdingPeriod,
      isLongTerm,
      taxRate_applied,
      exemptAmount,
      taxableGain,
      tax,
      netGain,
      effectiveRate,
      asset
    } = results;

    return (
      <View style={styles.resultsCard}>
        <Text style={styles.cardTitle}>Calculation Results</Text>
        
        <View style={styles.holdingPeriodInfo}>
          <Text style={styles.holdingPeriodLabel}>
            Holding Period: {holdingPeriod.months} months ({holdingPeriod.days} days)
          </Text>
          <View style={[
            styles.holdingPeriodBadge,
            { backgroundColor: isLongTerm ? colors.success : colors.warning }
          ]}>
            <Text style={styles.holdingPeriodBadgeText}>
              {isLongTerm ? 'Long Term' : 'Short Term'}
            </Text>
          </View>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Purchase Price</Text>
          <Text style={styles.resultValue}>{formatCurrency(purchase)}</Text>
        </View>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Sale Price</Text>
          <Text style={styles.resultValue}>{formatCurrency(sale)}</Text>
        </View>

        {asset.indexationAllowed && isLongTerm && (
          <>
            <View style={styles.resultItem}>
              <Text style={styles.resultLabel}>Indexed Cost</Text>
              <Text style={styles.resultValue}>{formatCurrency(indexedCost)}</Text>
            </View>
            
            <View style={styles.resultItem}>
              <Text style={styles.resultLabel}>Indexation Benefit</Text>
              <Text style={[styles.resultValue, { color: colors.success }]}>
                {formatCurrency(indexationBenefit)}
              </Text>
            </View>
          </>
        )}

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Capital Gain</Text>
          <Text style={[
            styles.resultValue, 
            { color: capitalGain >= 0 ? colors.success : colors.error }
          ]}>
            {formatCurrency(capitalGain)}
          </Text>
        </View>

        {exemptAmount > 0 && (
          <View style={styles.resultItem}>
            <Text style={styles.resultLabel}>Exempt Amount</Text>
            <Text style={[styles.resultValue, { color: colors.success }]}>
              {formatCurrency(exemptAmount)}
            </Text>
          </View>
        )}

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Taxable Gain</Text>
          <Text style={styles.resultValue}>{formatCurrency(taxableGain)}</Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Tax Rate Applied</Text>
          <Text style={styles.resultValue}>{taxRate_applied}%</Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Capital Gains Tax</Text>
          <Text style={[styles.resultValue, { color: colors.error }]}>
            {formatCurrency(tax)}
          </Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Net Gain After Tax</Text>
          <Text style={[
            styles.resultValue, 
            { 
              color: netGain >= 0 ? colors.success : colors.error,
              fontSize: 18,
              fontWeight: 'bold'
            }
          ]}>
            {formatCurrency(netGain)}
          </Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Effective Tax Rate</Text>
          <Text style={styles.resultValue}>{effectiveRate.toFixed(2)}%</Text>
        </View>
      </View>
    );
  };

  const renderScenarios = () => {
    if (!results) return null;

    const { scenarios } = results;

    return (
      <View style={styles.scenariosCard}>
        <TouchableOpacity
          style={styles.cardHeader}
          onPress={() => setShowScenarios(!showScenarios)}
        >
          <Text style={styles.cardTitle}>Investment Scenarios</Text>
          <Ionicons
            name={showScenarios ? 'chevron-up' : 'chevron-down'}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>
        
        {showScenarios && (
          <View style={styles.cardContent}>
            {scenarios.map((scenario, index) => (
              <View key={index} style={styles.scenarioItem}>
                <View style={styles.scenarioHeader}>
                  <Text style={styles.scenarioName}>{scenario.name}</Text>
                  <Text style={styles.scenarioType}>
                    {scenario.isLongTerm ? 'LTCG' : 'STCG'}
                  </Text>
                </View>
                <View style={styles.scenarioDetails}>
                  <Text style={styles.scenarioDetail}>
                    Sale Price: {formatCurrency(scenario.salePrice)}
                  </Text>
                  <Text style={styles.scenarioDetail}>
                    Holding: {scenario.holdingMonths} months
                  </Text>
                  <Text style={styles.scenarioDetail}>
                    Capital Gain: {formatCurrency(scenario.capitalGain)}
                  </Text>
                  <Text style={styles.scenarioDetail}>
                    Tax: {formatCurrency(scenario.tax)}
                  </Text>
                  <Text style={[styles.scenarioNetGain, { 
                    color: scenario.netGain >= 0 ? colors.success : colors.error 
                  }]}>
                    Net Gain: {formatCurrency(scenario.netGain)}
                  </Text>
                </View>
                <View style={styles.scenarioBar}>
                  <View 
                    style={[
                      styles.scenarioBarFill, 
                      { 
                        width: `${(scenario.netGain / Math.max(...scenarios.map(s => s.netGain))) * 100}%`,
                        backgroundColor: index === 0 ? colors.primary : index === 1 ? colors.warning : colors.success
                      }
                    ]} 
                  />
                </View>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  const renderExemptions = () => (
    <View style={styles.exemptionsCard}>
      <TouchableOpacity
        style={styles.cardHeader}
        onPress={() => setShowExemptions(!showExemptions)}
      >
        <Text style={styles.cardTitle}>Tax Exemptions & Deductions</Text>
        <Ionicons
          name={showExemptions ? 'chevron-up' : 'chevron-down'}
          size={24}
          color={colors.primary}
        />
      </TouchableOpacity>
      
      {showExemptions && (
        <View style={styles.cardContent}>
          <View style={styles.exemptionSection}>
            <Text style={styles.exemptionSectionTitle}>Section 54 - Residential Property</Text>
            <Text style={styles.exemptionText}>
              Exemption on LTCG from sale of residential property if invested in another residential property within 2 years.
            </Text>
          </View>

          <View style={styles.exemptionSection}>
            <Text style={styles.exemptionSectionTitle}>Section 54F - Other Assets</Text>
            <Text style={styles.exemptionText}>
              Exemption on LTCG from sale of any asset (except residential property) if invested in residential property.
            </Text>
          </View>

          <View style={styles.exemptionSection}>
            <Text style={styles.exemptionSectionTitle}>Section 54EC - Bonds</Text>
            <Text style={styles.exemptionText}>
              Exemption up to ₹50 lakh by investing in specified bonds (NHAI, REC) within 6 months.
            </Text>
          </View>

          <View style={styles.tipBox}>
            <Ionicons name="bulb" size={16} color={colors.warning} />
            <Text style={styles.tipText}>
              <Text style={{ fontWeight: 'bold' }}>Pro Tip:</Text> Plan your asset sales and 
              reinvestments to maximize tax exemptions under various sections.
            </Text>
          </View>
        </View>
      )}
    </View>
  );

  const renderInformation = () => (
    <View style={styles.infoContainer}>
      <Text style={styles.seoTitle}>📈 Complete Guide to Capital Gains Tax Calculator</Text>
      <Text style={styles.seoSubtitle}>Master Capital Gains Tax Calculation with Our Advanced Calculator</Text>
      
      <Text style={styles.seoText}>
        Our comprehensive capital gains tax calculator helps you accurately calculate tax liability on profits from selling capital assets. 
        Whether you're dealing with equity shares, mutual funds, real estate, or other investments, our tool provides precise calculations 
        considering holding periods, indexation benefits, and applicable tax rates for both short-term and long-term capital gains.
      </Text>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>🎯 What is Capital Gains Tax?</Text>
        <Text style={styles.seoText}>
          Capital gains tax is levied on the profit earned from selling capital assets like stocks, mutual funds, real estate, gold, 
          or bonds. The tax rate depends on the holding period and type of asset. Understanding capital gains tax is crucial for 
          effective investment planning and tax optimization.
        </Text>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>⏰ Short-term vs Long-term Capital Gains</Text>
        <View style={styles.comparisonContainer}>
          <View style={styles.comparisonItem}>
            <Text style={styles.comparisonTitle}>Short-term Capital Gains (STCG)</Text>
            <Text style={styles.comparisonText}>• Equity/MF: Less than 12 months</Text>
            <Text style={styles.comparisonText}>• Real Estate: Less than 24 months</Text>
            <Text style={styles.comparisonText}>• Debt Funds: Less than 36 months</Text>
            <Text style={styles.comparisonText}>• Tax Rate: As per income tax slab or flat rate</Text>
          </View>
          <View style={styles.comparisonItem}>
            <Text style={styles.comparisonTitle}>Long-term Capital Gains (LTCG)</Text>
            <Text style={styles.comparisonText}>• Equity/MF: 12 months or more</Text>
            <Text style={styles.comparisonText}>• Real Estate: 24 months or more</Text>
            <Text style={styles.comparisonText}>• Debt Funds: 36 months or more</Text>
            <Text style={styles.comparisonText}>• Tax Rate: Preferential rates with exemptions</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>💡 Key Features of Our Calculator</Text>
        <View style={styles.featuresList}>
          <View style={styles.featureItem}>
            <Ionicons name="calculator" size={16} color={colors.primary} />
            <Text style={styles.featureText}>Accurate tax calculations for all asset types</Text>
          </View>
          <View style={styles.featureItem}>
            <Ionicons name="trending-up" size={16} color={colors.success} />
            <Text style={styles.featureText}>Automatic indexation benefit calculation</Text>
          </View>
          <View style={styles.featureItem}>
            <Ionicons name="time" size={16} color={colors.warning} />
            <Text style={styles.featureText}>Holding period analysis and classification</Text>
          </View>
          <View style={styles.featureItem}>
            <Ionicons name="shield-checkmark" size={16} color={colors.info} />
            <Text style={styles.featureText}>Tax exemption scenarios and planning</Text>
          </View>
          <View style={styles.featureItem}>
            <Ionicons name="bar-chart" size={16} color={colors.primary} />
            <Text style={styles.featureText}>Visual breakdown with interactive charts</Text>
          </View>
          <View style={styles.featureItem}>
            <Ionicons name="document-text" size={16} color={colors.success} />
            <Text style={styles.featureText}>Detailed scenario analysis and comparisons</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>📊 Tax Rates for Different Assets (FY 2024-25)</Text>
        <View style={styles.taxRateTable}>
          <View style={styles.taxRateRow}>
            <Text style={styles.taxRateHeader}>Asset Type</Text>
            <Text style={styles.taxRateHeader}>STCG Rate</Text>
            <Text style={styles.taxRateHeader}>LTCG Rate</Text>
            <Text style={styles.taxRateHeader}>Holding Period</Text>
          </View>
          <View style={styles.taxRateRow}>
            <Text style={styles.taxRateCell}>Equity Shares/MF</Text>
            <Text style={styles.taxRateCell}>15%</Text>
            <Text style={styles.taxRateCell}>10% (>₹1L)</Text>
            <Text style={styles.taxRateCell}>12 months</Text>
          </View>
          <View style={styles.taxRateRow}>
            <Text style={styles.taxRateCell}>Debt Mutual Funds</Text>
            <Text style={styles.taxRateCell}>Slab Rate</Text>
            <Text style={styles.taxRateCell}>20% + Index</Text>
            <Text style={styles.taxRateCell}>36 months</Text>
          </View>
          <View style={styles.taxRateRow}>
            <Text style={styles.taxRateCell}>Real Estate</Text>
            <Text style={styles.taxRateCell}>Slab Rate</Text>
            <Text style={styles.taxRateCell}>20% + Index</Text>
            <Text style={styles.taxRateCell}>24 months</Text>
          </View>
          <View style={styles.taxRateRow}>
            <Text style={styles.taxRateCell}>Gold/Jewelry</Text>
            <Text style={styles.taxRateCell}>Slab Rate</Text>
            <Text style={styles.taxRateCell}>20% + Index</Text>
            <Text style={styles.taxRateCell}>36 months</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>🔧 How to Use the Capital Gains Calculator</Text>
        <View style={styles.stepsList}>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}><Text style={styles.stepNumberText}>1</Text></View>
            <Text style={styles.stepText}>Select your asset type (Equity, Debt, Real Estate, etc.)</Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}><Text style={styles.stepNumberText}>2</Text></View>
            <Text style={styles.stepText}>Enter the original purchase price of the asset</Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}><Text style={styles.stepNumberText}>3</Text></View>
            <Text style={styles.stepText}>Input the sale price and transaction dates</Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}><Text style={styles.stepNumberText}>4</Text></View>
            <Text style={styles.stepText}>Choose your income tax slab for STCG calculation</Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}><Text style={styles.stepNumberText}>5</Text></View>
            <Text style={styles.stepText}>Get instant results with tax breakdown and net gains</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>💰 Tax Saving Strategies</Text>
        <View style={styles.strategiesList}>
          <View style={styles.strategyItem}>
            <Ionicons name="time" size={16} color={colors.primary} />
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Hold for Long-term</Text>
              <Text style={styles.strategyText}>Hold assets beyond the minimum period to qualify for LTCG rates</Text>
            </View>
          </View>
          <View style={styles.strategyItem}>
            <Ionicons name="swap-horizontal" size={16} color={colors.success} />
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Loss Harvesting</Text>
              <Text style={styles.strategyText}>Offset gains with losses from other investments</Text>
            </View>
          </View>
          <View style={styles.strategyItem}>
            <Ionicons name="home" size={16} color={colors.warning} />
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Section 54 Exemption</Text>
              <Text style={styles.strategyText}>Reinvest in residential property to claim exemptions</Text>
            </View>
          </View>
          <View style={styles.strategyItem}>
            <Ionicons name="document" size={16} color={colors.info} />
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Section 54EC Bonds</Text>
              <Text style={styles.strategyText}>Invest up to ₹50 lakh in specified bonds within 6 months</Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.tipBox}>
        <Ionicons name="bulb" size={16} color={colors.warning} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>Pro Tip:</Text> Use our calculator to compare different selling scenarios 
          and optimize your tax liability. Consider the impact of holding period and available exemptions before making investment decisions.
        </Text>
      </View>
    </View>
  );

  const renderSEOContent = () => (
    <View style={styles.infoContainer}>
      <Text style={styles.seoTitle}>Advanced Capital Gains Tax Planning & Investment Strategies</Text>
      <Text style={styles.seoSubtitle}>Comprehensive Guide to Optimizing Your Investment Returns</Text>
      
      <Text style={styles.seoText}>
        Capital gains taxation is a critical aspect of investment planning that significantly impacts your overall returns. 
        Our advanced calculator helps you navigate complex tax scenarios and make informed investment decisions across 
        various asset classes including equity, debt, real estate, and alternative investments.
      </Text>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>📊 Asset Class Comparison & Tax Implications</Text>
        <View style={styles.comparisonContainer}>
          <View style={styles.comparisonItem}>
            <Text style={styles.comparisonTitle}>Equity Investments</Text>
            <Text style={styles.comparisonText}>• STCG: 15% (&lt; 12 months)</Text>
            <Text style={styles.comparisonText}>• LTCG: 10% above ₹1L (≥ 12 months)</Text>
            <Text style={styles.comparisonText}>• No indexation benefit</Text>
            <Text style={styles.comparisonText}>• Securities Transaction Tax applicable</Text>
          </View>
          <View style={styles.comparisonItem}>
            <Text style={styles.comparisonTitle}>Debt Investments</Text>
            <Text style={styles.comparisonText}>• STCG: As per income slab (&lt; 36 months)</Text>
            <Text style={styles.comparisonText}>• LTCG: 20% with indexation (≥ 36 months)</Text>
            <Text style={styles.comparisonText}>• Indexation benefit available</Text>
            <Text style={styles.comparisonText}>• Lower volatility, steady returns</Text>
          </View>
          <View style={styles.comparisonItem}>
            <Text style={styles.comparisonTitle}>Real Estate</Text>
            <Text style={styles.comparisonText}>• STCG: As per income slab (&lt; 24 months)</Text>
            <Text style={styles.comparisonText}>• LTCG: 20% with indexation (≥ 24 months)</Text>
            <Text style={styles.comparisonText}>• Multiple exemptions available</Text>
            <Text style={styles.comparisonText}>• High transaction costs</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>🎯 Advanced Calculator Features</Text>
        <View style={styles.featuresList}>
          <View style={styles.featureItem}>
            <Ionicons name="calculator" size={16} color={colors.primary} />
            <Text style={styles.featureText}>Multi-asset class support with specific tax rules</Text>
          </View>
          <View style={styles.featureItem}>
            <Ionicons name="trending-up" size={16} color={colors.success} />
            <Text style={styles.featureText}>Indexation calculation for eligible investments</Text>
          </View>
          <View style={styles.featureItem}>
            <Ionicons name="shield-checkmark" size={16} color={colors.info} />
            <Text style={styles.featureText}>Exemption analysis under various sections</Text>
          </View>
          <View style={styles.featureItem}>
            <Ionicons name="analytics" size={16} color={colors.warning} />
            <Text style={styles.featureText}>Scenario comparison for optimal timing</Text>
          </View>
          <View style={styles.featureItem}>
            <Ionicons name="document-text" size={16} color={colors.error} />
            <Text style={styles.featureText}>Tax liability breakdown and net returns</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>💡 Strategic Tax Planning Techniques</Text>
        <View style={styles.strategiesList}>
          <View style={styles.strategyItem}>
            <Ionicons name="calendar" size={16} color={colors.primary} />
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Timing Optimization</Text>
              <Text style={styles.strategyText}>Plan your asset sales to qualify for long-term capital gains rates. Consider spreading sales across financial years to manage tax brackets effectively.</Text>
            </View>
          </View>
          <View style={styles.strategyItem}>
            <Ionicons name="swap-horizontal" size={16} color={colors.success} />
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Loss Harvesting Strategy</Text>
              <Text style={styles.strategyText}>Systematically realize losses to offset gains. STCG losses can offset both STCG and LTCG, while LTCG losses can only offset LTCG.</Text>
            </View>
          </View>
          <View style={styles.strategyItem}>
            <Ionicons name="home" size={16} color={colors.warning} />
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Reinvestment Exemptions</Text>
              <Text style={styles.strategyText}>Utilize Section 54, 54F, and 54EC exemptions by reinvesting in eligible assets within specified timeframes to reduce tax liability.</Text>
            </View>
          </View>
          <View style={styles.strategyItem}>
            <Ionicons name="people" size={16} color={colors.info} />
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Family Tax Planning</Text>
              <Text style={styles.strategyText}>Distribute investments among family members in lower tax brackets to optimize overall tax efficiency and utilize multiple exemption limits.</Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>📋 Step-by-Step Calculation Process</Text>
        <View style={styles.stepsList}>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}><Text style={styles.stepNumberText}>1</Text></View>
            <Text style={styles.stepText}>Determine the holding period to classify as STCG or LTCG based on asset type</Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}><Text style={styles.stepNumberText}>2</Text></View>
            <Text style={styles.stepText}>Calculate the cost of acquisition including purchase price, brokerage, and improvement costs</Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}><Text style={styles.stepText}>3</Text></View>
            <Text style={styles.stepText}>Apply indexation benefit if eligible (debt funds, real estate, gold held for qualifying period)</Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}><Text style={styles.stepNumberText}>4</Text></View>
            <Text style={styles.stepText}>Deduct cost of transfer including brokerage, registration fees, and legal expenses</Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}><Text style={styles.stepNumberText}>5</Text></View>
            <Text style={styles.stepText}>Apply applicable tax rate and calculate final tax liability and net proceeds</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>⚖️ Legal Compliance & Documentation</Text>
        <Text style={styles.seoText}>
          Maintain comprehensive records of all transactions including purchase receipts, improvement costs, 
          sale deeds, and brokerage statements. Ensure compliance with advance tax payment requirements for 
          capital gains exceeding ₹10,000. Consider professional consultation for complex scenarios involving 
          multiple asset classes or international investments.
        </Text>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>🚫 Common Mistakes to Avoid</Text>
        <View style={styles.strategiesList}>
          <View style={styles.strategyItem}>
            <Ionicons name="warning" size={16} color={colors.error} />
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Ignoring Holding Period Rules</Text>
              <Text style={styles.strategyText}>Different assets have different holding periods for LTCG qualification. Selling even a day early can significantly increase tax liability.</Text>
            </View>
          </View>
          <View style={styles.strategyItem}>
            <Ionicons name="document" size={16} color={colors.warning} />
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Poor Record Keeping</Text>
              <Text style={styles.strategyText}>Inadequate documentation can lead to loss of indexation benefits and difficulty in claiming legitimate deductions.</Text>
            </View>
          </View>
          <View style={styles.strategyItem}>
            <Ionicons name="time" size={16} color={colors.info} />
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Missing Exemption Deadlines</Text>
              <Text style={styles.strategyText}>Exemptions under sections 54, 54F, and 54EC have strict timelines. Missing these can result in unnecessary tax payments.</Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>📈 Success Stories & Examples</Text>
        <Text style={styles.seoText}>
          <Text style={{ fontWeight: 'bold' }}>Case Study 1:</Text> An investor saved ₹2.8 lakhs in taxes by timing 
          the sale of equity mutual funds to qualify for LTCG treatment instead of STCG, reducing tax rate from 30% to 10%.
        </Text>
        <Text style={styles.seoText}>
          <Text style={{ fontWeight: 'bold' }}>Case Study 2:</Text> A real estate investor utilized Section 54 exemption 
          by purchasing another residential property, completely eliminating ₹15 lakhs in capital gains tax liability.
        </Text>
        <Text style={styles.seoText}>
          <Text style={{ fontWeight: 'bold' }}>Case Study 3:</Text> Strategic loss harvesting helped an investor offset 
          ₹5 lakhs in capital gains, saving approximately ₹1.5 lakhs in taxes while maintaining portfolio allocation.
        </Text>
      </View>

      <View style={styles.tipBox}>
        <Ionicons name="bulb" size={16} color={colors.warning} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>Expert Tip:</Text> Use our calculator to model different scenarios before making investment decisions. 
          Consider the total cost of ownership including taxes, transaction costs, and opportunity costs to make optimal choices.
        </Text>
      </View>
    </View>
  );

  const renderFAQ = () => {
    const faqData = [
      {
        question: "What is the difference between STCG and LTCG?",
        answer: "Short-term capital gains (STCG) apply to assets held for less than the specified period, while long-term capital gains (LTCG) apply to assets held longer. LTCG generally has lower tax rates and may include indexation benefits."
      },
      {
        question: "How does indexation work?",
        answer: "Indexation adjusts the purchase price for inflation using the Cost Inflation Index. This reduces the taxable capital gain. It's available for debt funds, real estate, and gold held for more than 36/24 months."
      },
      {
        question: "Can I offset capital gains with capital losses?",
        answer: "Yes, capital losses can be set off against capital gains. STCG losses can offset both STCG and LTCG, while LTCG losses can only offset LTCG. Unabsorbed losses can be carried forward for 8 years."
      },
      {
        question: "What are the exemptions available for capital gains?",
        answer: "Section 54 (residential property), 54F (other assets to residential property), and 54EC (investment in bonds) provide exemptions. Each has specific conditions regarding reinvestment amount and time limits."
      },
      {
        question: "How is capital gains tax calculated on equity mutual funds?",
        answer: "STCG (held <12 months): 15% flat rate. LTCG (held ≥12 months): 10% on gains above ₹1 lakh per year, without indexation benefit."
      }
    ];

    return (
      <View style={styles.faqContainer}>
        <TouchableOpacity
          style={styles.faqHeader}
          onPress={() => setShowFAQ(!showFAQ)}
        >
          <Text style={styles.faqTitle}>Frequently Asked Questions</Text>
          <Ionicons
            name={showFAQ ? 'chevron-up' : 'chevron-down'}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>
        
        {showFAQ && (
          <View style={styles.faqContent}>
            {faqData.map((faq, index) => (
              <View key={index} style={styles.faqItem}>
                <Text style={styles.faqQuestion}>{faq.question}</Text>
                <Text style={styles.faqAnswer}>{faq.answer}</Text>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      backgroundColor: colors.surface,
      paddingVertical: 40,
      paddingHorizontal: 20,
      alignItems: 'center',
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    headerTitle: {
      fontSize: 32,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
      textAlign: 'center',
    },
    headerSubtitle: {
      fontSize: 16,
      color: colors.textSecondary,
      textAlign: 'center',
      lineHeight: 24,
    },
    content: {
      flexDirection: isMobile ? 'column' : 'row',
      padding: 20,
      gap: 20,
      width: '100%',
    },
    leftPanel: {
      flex: isMobile ? 1 : 1.2,
    },
    rightPanel: {
      flex: 1,
      gap: 20,
    },
    calculatorCard: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    cardTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 20,
    },
    inputGroup: {
      marginBottom: 20,
    },
    label: {
      fontSize: 14,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    inputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      backgroundColor: colors.background,
    },
    currencySymbol: {
      fontSize: 16,
      color: colors.textSecondary,
      paddingLeft: 12,
    },
    textInput: {
      flex: 1,
      height: 48,
      paddingHorizontal: 12,
      fontSize: 16,
      color: colors.text,
    },
    dateInput: {
      height: 48,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      paddingHorizontal: 12,
      fontSize: 16,
      color: colors.text,
      backgroundColor: colors.background,
    },
    helperText: {
      fontSize: 12,
      color: colors.textSecondary,
      marginTop: 4,
    },
    assetTypeContainer: {
      flexDirection: isMobile ? 'column' : 'row',
      gap: 12,
      marginTop: 8,
    },
    assetTypeCard: {
      backgroundColor: colors.surface,
      borderRadius: 8,
      padding: 16,
      flex: 1,
      borderWidth: 1,
      borderColor: colors.border,
      alignItems: 'center',
    },
    assetTypeCardActive: {
      backgroundColor: colors.primary + '20',
      borderColor: colors.primary,
    },
    assetTypeIndicator: {
      width: 12,
      height: 12,
      borderRadius: 6,
      marginBottom: 8,
    },
    assetTypeName: {
      fontSize: 14,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 4,
      textAlign: 'center',
    },
    assetTypeNameActive: {
      color: colors.primary,
    },
    assetTypeDescription: {
      fontSize: 12,
      color: colors.textSecondary,
      marginBottom: 8,
      textAlign: 'center',
    },
    assetTypeDescriptionActive: {
      color: colors.primary,
    },
    assetTypeRate: {
      fontSize: 10,
      color: colors.textSecondary,
      fontWeight: '500',
      textAlign: 'center',
    },
    assetTypeRateActive: {
      color: colors.primary,
    },
    taxSlabContainer: {
      flexDirection: isMobile ? 'column' : 'row',
      gap: 8,
      marginTop: 8,
    },
    taxSlabButton: {
      backgroundColor: colors.surface,
      borderRadius: 8,
      padding: 12,
      flex: 1,
      borderWidth: 1,
      borderColor: colors.border,
      alignItems: 'center',
    },
    taxSlabButtonActive: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    taxSlabLabel: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 4,
    },
    taxSlabLabelActive: {
      color: colors.background,
    },
    taxSlabDescription: {
      fontSize: 10,
      color: colors.textSecondary,
      textAlign: 'center',
    },
    taxSlabDescriptionActive: {
      color: colors.background,
    },
    chartCard: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
      alignItems: 'center',
    },
    chartContainer: {
      alignItems: 'center',
      marginVertical: 20,
    },
    chartLegend: {
      marginTop: 20,
      gap: 8,
    },
    legendItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    legendColor: {
      width: 12,
      height: 12,
      borderRadius: 6,
    },
    legendText: {
      fontSize: 14,
      color: colors.textSecondary,
    },
    resultsCard: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    holdingPeriodInfo: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 20,
      padding: 12,
      backgroundColor: colors.surface,
      borderRadius: 8,
    },
    holdingPeriodLabel: {
      fontSize: 14,
      color: colors.text,
      fontWeight: '500',
    },
    holdingPeriodBadge: {
      paddingHorizontal: 12,
      paddingVertical: 4,
      borderRadius: 12,
    },
    holdingPeriodBadgeText: {
      fontSize: 12,
      color: colors.background,
      fontWeight: 'bold',
    },
    resultItem: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    resultLabel: {
      fontSize: 14,
      color: colors.textSecondary,
    },
    resultValue: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
    },
    scenariosCard: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      margin: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    cardHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 4,
    },
    cardContent: {
      marginTop: 20,
    },
    scenarioItem: {
      marginBottom: 16,
      padding: 12,
      backgroundColor: colors.background,
      borderRadius: 8,
    },
    scenarioHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 8,
    },
    scenarioName: {
      fontSize: 14,
      fontWeight: '600',
      color: colors.text,
    },
    scenarioType: {
      fontSize: 12,
      color: colors.primary,
      fontWeight: '500',
      backgroundColor: colors.primary + '20',
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: 4,
    },
    scenarioDetails: {
      marginBottom: 8,
    },
    scenarioDetail: {
      fontSize: 12,
      color: colors.textSecondary,
      marginBottom: 2,
    },
    scenarioNetGain: {
      fontSize: 13,
      fontWeight: '600',
      marginTop: 4,
    },
    scenarioBar: {
      height: 4,
      backgroundColor: colors.border,
      borderRadius: 2,
      overflow: 'hidden',
    },
    scenarioBarFill: {
      height: '100%',
      borderRadius: 2,
    },
    exemptionsCard: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      margin: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    exemptionSection: {
      marginBottom: 16,
    },
    exemptionSectionTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    exemptionText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    infoContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      margin: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    seoTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
      textAlign: 'center',
    },
    seoSubtitle: {
      fontSize: 16,
      color: colors.primary,
      marginBottom: 16,
      textAlign: 'center',
      fontWeight: '600',
    },
    seoText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 22,
      marginBottom: 16,
    },
    seoSection: {
      marginBottom: 20,
    },
    seoSectionTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 12,
    },
    comparisonContainer: {
      flexDirection: isMobile ? 'column' : 'row',
      gap: 16,
      marginTop: 12,
    },
    comparisonItem: {
      flex: 1,
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: 16,
      borderWidth: 1,
      borderColor: colors.border,
    },
    comparisonTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    comparisonText: {
      fontSize: 13,
      color: colors.textSecondary,
      marginBottom: 4,
    },
    featuresList: {
      marginTop: 12,
    },
    featureItem: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 12,
      paddingVertical: 8,
    },
    featureText: {
      fontSize: 14,
      color: colors.textSecondary,
      marginLeft: 12,
      flex: 1,
    },
    taxRateTable: {
      marginTop: 12,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      overflow: 'hidden',
    },
    taxRateRow: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    taxRateHeader: {
      flex: 1,
      fontSize: 12,
      fontWeight: 'bold',
      color: colors.text,
      padding: 12,
      backgroundColor: colors.primary + '10',
      textAlign: 'center',
    },
    taxRateCell: {
      flex: 1,
      fontSize: 11,
      color: colors.textSecondary,
      padding: 12,
      textAlign: 'center',
      backgroundColor: colors.background,
    },
    stepsList: {
      marginTop: 12,
    },
    stepItem: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: 16,
    },
    stepNumber: {
      width: 24,
      height: 24,
      borderRadius: 12,
      backgroundColor: colors.primary,
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 12,
    },
    stepNumberText: {
      fontSize: 12,
      fontWeight: 'bold',
      color: colors.background,
    },
    stepText: {
      fontSize: 14,
      color: colors.textSecondary,
      flex: 1,
      lineHeight: 20,
    },
    strategiesList: {
      marginTop: 12,
    },
    strategyItem: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: 16,
      padding: 12,
      backgroundColor: colors.background,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border,
    },
    strategyContent: {
      marginLeft: 12,
      flex: 1,
    },
    strategyTitle: {
      fontSize: 14,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 4,
    },
    strategyText: {
      fontSize: 13,
      color: colors.textSecondary,
      lineHeight: 18,
    },
    infoTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    infoSubtitle: {
      fontSize: 16,
      color: colors.primary,
      marginBottom: 16,
      fontWeight: '600',
    },
    infoText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
      marginBottom: 16,
    },
    tipBox: {
      flexDirection: 'row',
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: 12,
      marginTop: 8,
    },
    tipText: {
      fontSize: 13,
      color: colors.textSecondary,
      marginLeft: 8,
      flex: 1,
      lineHeight: 18,
    },
    faqContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    faqHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 4,
    },
    faqTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
    },
    faqContent: {
      marginTop: 20,
    },
    faqItem: {
      marginBottom: 20,
      paddingBottom: 16,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    faqQuestion: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    faqAnswer: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    fullWidthSection: {
      paddingHorizontal: 20,
    },
  });

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {renderHeader()}
      
      <View style={styles.content}>
        <View style={styles.leftPanel}>
          {renderCalculator()}
        </View>
        
        <View style={styles.rightPanel}>
          {renderDonutChart()}
          {renderResults()}
        </View>
      </View>
      
      {renderScenarios()}
      {renderExemptions()}
      {renderInformation()}
      {renderSEOContent()}
      
      <View style={styles.fullWidthSection}>
        {renderFAQ()}
      </View>
      
      <Footer />
    </ScrollView>
  );
};

export default CapitalGainsCalculatorScreen;